import z from "zod";

import { apiRequest } from "./apiUtils";
import { WorkerSummarySchema } from "./starredWorkerApi";
import type { WorkerSummary } from "./starredWorkerApi";

export const UpcomingShiftItemSchema = z.object({
  shift_id: z.number(),
  name: z.string().nullable().optional(),
  role_name: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
  wage: z.union([z.string(), z.number()]).nullable().optional(),
  wage_unit: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  filled: z.boolean().default(false),
  assigned_worker: WorkerSummarySchema.nullable().optional(),
});

export type UpcomingShiftItem = z.infer<typeof UpcomingShiftItemSchema>;

const UpcomingShiftsResponseSchema = z.object({
  success: z.boolean().default(true),
  message: z.string().nullable().optional(),
  shifts: z.array(UpcomingShiftItemSchema).default([]),
});

const PreviousWorkersResponseSchema = z.object({
  success: z.boolean().default(true),
  message: z.string().nullable().optional(),
  workers: z.array(WorkerSummarySchema).default([]),
});

export async function listUpcomingShifts(
  days = 14,
): Promise<Array<UpcomingShiftItem>> {
  const data = await apiRequest<unknown>(
    `business/upcoming-shifts?days=${days}`,
    { method: "GET" },
  );
  return UpcomingShiftsResponseSchema.parse(data).shifts;
}

export async function listPreviousWorkers(): Promise<Array<WorkerSummary>> {
  const data = await apiRequest<unknown>("business/previous-workers", {
    method: "GET",
  });
  return PreviousWorkersResponseSchema.parse(data).workers;
}

export const SeriesDaySchema = z.object({
  shift_id: z.number(),
  date: z.string(),
  start_time: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  filled: z.boolean().default(false),
  /** Worker view only: the caller's own status on that day. */
  my_status: z.enum(["applied", "offered", "approved"]).nullable().optional(),
});
export type SeriesDay = z.infer<typeof SeriesDaySchema>;

export const ShiftDetailSchema = z.object({
  success: z.boolean().default(true),
  message: z.string().nullable().optional(),
  shift_id: z.number(),
  name: z.string().nullable().optional(),
  role_name: z.string().nullable().optional(),
  role_description: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
  wage: z.union([z.string(), z.number()]).nullable().optional(),
  wage_unit: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  filled: z.boolean().default(false),
  /** Worker view: an employer offered this shift and is waiting on the worker's Accept/Decline. */
  offer_pending: z.boolean().default(false),
  days: z.array(z.string()).nullable().optional(),
  repeat: z.string().nullable().optional(),
  /** Set when this day was posted as part of a multi-day series. */
  series_id: z.string().nullable().optional(),
  /** Every day of the series (including this one), soonest first. */
  series_days: z.array(SeriesDaySchema).default([]),
  min_rating: z.number().nullable().optional(),
  located_within: z.number().nullable().optional(),
  allow_instant_acceptance: z.boolean().nullable().optional(),
  require_training: z.boolean().nullable().optional(),
  address: z
    .object({
      address1: z.string().nullable().optional(),
      state: z.string().nullable().optional(),
      zipcode: z.string().nullable().optional(),
      location: z.string().nullable().optional(),
      latitude: z.number().nullable().optional(),
      longitude: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  business: z
    .object({
      id: z.number(),
      name: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  assigned_worker: WorkerSummarySchema.nullable().optional(),
  applicants: z.array(WorkerSummarySchema).default([]),
  employer_review: z
    .object({
      average: z.number().nullable().optional(),
      axes: z.record(z.string(), z.number()).default({}),
    })
    .nullable()
    .optional(),
});

export type ShiftDetail = z.infer<typeof ShiftDetailSchema>;

export async function getShiftDetail(shiftId: number): Promise<ShiftDetail> {
  const data = await apiRequest<unknown>(`business/shift/${shiftId}`, {
    method: "GET",
  });
  return ShiftDetailSchema.parse(data);
}
